import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { Alert, Badge, Button, Card } from '../ui';
import {
  getProjectTestRunStatus,
  startProjectTestRun,
} from '../../services/projectService';

const STATUS_PRESENTATION = {
  not_started: {
    label: 'Not started',
    variant: 'gray',
    description: 'Ready for the first bounded live test run.',
  },
  running: {
    label: 'Running',
    variant: 'blue',
    description: 'Matching live messages are being admitted to the report queue.',
  },
  limit_reached: {
    label: '20-message run complete',
    variant: 'green',
    description: 'All 20 accepted source messages were delivered and the project is now inactive.',
  },
  draining: {
    label: '20 slots reserved · delivering',
    variant: 'blue',
    description: 'New source messages are paused while the accepted report messages finish delivery.',
  },
  stopped: {
    label: 'Stopped manually',
    variant: 'yellow',
    description: 'The previous run was stopped before reaching the limit.',
  },
  ready_after_correction: {
    label: 'Correction saved',
    variant: 'indigo',
    description: 'A keyword/minus-word revision is ready for a new manual test run.',
  },
};

const getApiError = (error) => (
  error?.response?.data?.error || error?.message || 'Project test run request failed'
);

function ProjectTestRunPanel({ project, isNewProject, onStatusChange }) {
  const [localError, setLocalError] = useState('');
  const projectId = project?.id;
  const statusQuery = useQuery(
    ['project-test-run', projectId, project?.keyword_config_revision],
    () => getProjectTestRunStatus(projectId),
    {
      enabled: Boolean(projectId && !isNewProject),
      refetchInterval: (response) => {
        const responseStatus = response?.data;
        return (
          responseStatus?.status === 'running' ||
          responseStatus?.is_draining === true ||
          (responseStatus?.status === 'limit_reached' && responseStatus?.is_active === true)
        ) ? 3000 : false;
      },
      onSuccess: (response) => {
        if (response?.data && onStatusChange) {
          onStatusChange(response.data);
        }
      },
    }
  );

  const startMutation = useMutation(
    (keywordRevision) => startProjectTestRun(projectId, keywordRevision),
    {
      onSuccess: (response) => {
        setLocalError('');
        if (response?.data && onStatusChange) {
          onStatusChange(response.data);
        }
        statusQuery.refetch();
      },
      onError: (error) => setLocalError(getApiError(error)),
    }
  );

  if (isNewProject || !projectId) {
    return null;
  }

  const status = statusQuery.data?.data || {
    status: project.test_run_status || 'not_started',
    matched_count: project.test_run_matched_count || 0,
    limit: 20,
    remaining: Math.max(0, 20 - (project.test_run_matched_count || 0)),
    keyword_config_revision: project.keyword_config_revision || 1,
    is_active: Boolean(project.is_active),
    is_draining: project.test_run_status === 'limit_reached' && project.is_active === true,
    can_start: project.test_run_status === 'not_started',
    correction_required: false,
  };
  const isDraining = status.is_draining === true ||
    (status.status === 'limit_reached' && status.is_active === true);
  const presentation = isDraining
    ? STATUS_PRESENTATION.draining
    : (STATUS_PRESENTATION[status.status] || STATUS_PRESENTATION.not_started);
  const isRunning = status.status === 'running';

  return (
    <Card className="mb-6 border border-blue-200">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-medium text-gray-900">20-message live test run</h2>
            <Badge variant={presentation.variant}>{presentation.label}</Badge>
          </div>
          <p className="mt-1 text-sm text-gray-600">{presentation.description}</p>
          <p className="mt-1 text-xs text-gray-500">
            This is a bounded live project run. It is separate from “Test Keywords”,
            which checks one typed message without activating the project.
          </p>
        </div>

        <div className="text-right">
          <div className="text-2xl font-semibold text-gray-900">
            {status.matched_count}/{status.limit}
          </div>
          <div className="text-xs text-gray-500">
            occupied source slots · revision {status.keyword_config_revision}
          </div>
        </div>
      </div>

      <div className="mt-4 h-2 overflow-hidden rounded-full bg-gray-200">
        <div
          className="h-full bg-blue-600 transition-all"
          style={{ width: `${Math.min(100, (status.matched_count / status.limit) * 100)}%` }}
        />
      </div>

      {(localError || statusQuery.error) && (
        <Alert variant="error" className="mt-4">
          {localError || getApiError(statusQuery.error)}
        </Alert>
      )}

      {status.correction_required && (
        <Alert variant="warning" className="mt-4">
          A rerun is locked. Correct and save at least one canonical keyword or
          minus-word first; changing only project activation does not unlock it.
        </Alert>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <span className="text-sm text-gray-600">
          {isDraining
            ? 'All 20 source slots are reserved. Delivery is still draining; failed slots reopen automatically.'
            : (isRunning
              ? `${status.remaining} source slots remaining; the project stays active until all 20 are delivered.`
              : 'Starting is always manual and resets the counter only for a permitted keyword revision.')}
        </span>
        <Button
          type="button"
          variant={isRunning ? 'secondary' : 'primary'}
          disabled={!status.can_start || isRunning || isDraining || statusQuery.isLoading}
          isLoading={startMutation.isLoading}
          onClick={() => startMutation.mutate(status.keyword_config_revision)}
        >
          {isDraining
            ? 'Delivering accepted messages'
            : (isRunning ? 'Test run in progress' : 'Start 20-message test run')}
        </Button>
      </div>
    </Card>
  );
}

export default ProjectTestRunPanel;
